// hooks
import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useHttp from "hooks/use-http";

// material styles
import {
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

// material icons
import CheckIcon from "@mui/icons-material/Check";

// request methods
import {
  deleteRecordByIdRequest,
  getRecordByIdRequest,
  getRecordSubCategoryRequest,
  updateRecordRequest,
} from "api/requests/records";

// components
import LoadingCM from "components/shared/Loading";
import DrawerCM from "components/shared/Drawer";
import ProgressBarCM from "components/shared/ProgressBar";
import Upload from "components/shared/Upload";
import UploadVideoCM from "components/shared/Video/Upload";

// helper
import Toast from "helper/toast";

// form validate
import { pageItemSchema, validateImg } from "validations";

// styled component
import {
  StyledStack,
  StyledTitle,
  ButtonContainer,
  StyledMenuItem,
} from "./editRecords.style";

// editor library
import Editor, { buttonList } from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import ReactHookFormSelect from "components/shared/ReactHookFormSelect";
import DeleteModal from "components/shared/Modal/Delete";
import { useNavigate, useParams } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import BackButton from "components/shared/BackButton";

export default function EditRecords() {
  const {
    request,
    loading,
    setLoading,
    isUploading,
    uploadProgress,
    setUploadProgress,
  } = useHttp(false);

  const navigate = useNavigate();
  const { records_id } = useParams();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(pageItemSchema),
  });

  // checkbox state
  const [checked, setChecked] = useState(false);

  const handleCheckChange = () => setChecked((state) => !state);

  // editor value
  const [text, setText] = useState("");

  // upload image and video state
  const [recordsImage, setRecordsImage] = useState({
    url: null,
    isChanged: false,
  });
  const [recordsVideo, setRecordsVideo] = useState({
    url: null,
    isChanged: false,
  });

  // text editor value handler method
  const handleChangeText = (text) => setText(text);

  // select option state
  const [subCategoryList, setSubCategoryList] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  // delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  //handle delete modal
  const handelShowModal = () => setShowDeleteModal((state) => !state);

  // delete record by id
  const handleDeleteItem = useCallback(
    async (id) => {
      setLoading(false);
      const res = await request({
        url: () => deleteRecordByIdRequest(id),
      });
      if (res) {
        Toast({ type: "success", title: "حذف سوابق " });
        navigate("/records");
      }
      handelShowModal();
    },
    [setLoading]
  );

  // edit records
  const onSubmit = async (data) => {
    if (!text) return Toast({ type: "empty", title: "فیلد توضیحات " });
    if (!recordsImage.url) return Toast({ type: "empty", title: "فیلد عکس " });
    const res = await request({
      url: () =>
        updateRecordRequest(
          {
            id: records_id,
            title: data.title,
            category_id: selectedSubCategory,
            text,
            show_in_home_page: checked ? 1 : 0,
            main_image: recordsImage,
            video: recordsVideo ? recordsVideo : null,
          },
          (progress) => setUploadProgress(progress)
        ),
      isFile: true,
    });
    if (res) {
      Toast({ type: "success", title: "افزودن سوابق" });
      navigate("/records");
    }
  };

  // uploaded image handler method
  const handleChangeRecordsImage = (val) => {
    validateImg(val);
    setRecordsImage({ url: val, isChanged: true });
  };

  // uploaded video handler method
  const handleChangeRecordsVideo = (val) =>
    setRecordsVideo({ url: val, isChanged: true });

  // get record by id data
  const getRecordByIdData = useCallback(async () => {
    const subCategoryRes = await request({
      url: getRecordSubCategoryRequest,
      values: {
        skip: 0,
        limit: 100,
        search: null,
      },
      isMulty: true,
    });
    setLoading(false);
    if (subCategoryRes) {
      let transformedData = subCategoryRes.data?.data.map((items) => ({
        id: items.id,
        title: items.title,
      }));
      setSubCategoryList(transformedData);
    }

    const recordRes = await request({
      url: () => getRecordByIdRequest({ id: records_id }),
    });
    setLoading(false);
    if (recordRes) {
      setText(recordRes.data.text);
      setRecordsImage({ url: recordRes.data?.main_image, isWeb: true });
      if (recordRes.data.video) {
        setRecordsVideo({ url: recordRes.data?.video, isWeb: true });
      }
      setChecked(recordRes.data?.show_in_home_page);
      reset({
        title: recordRes.data?.title,
      });
      if (recordRes.data.category_id) {
        reset({
          subCategory: recordRes.data.category_id,
          title: recordRes.data?.title,
        });
        setSelectedSubCategory(recordRes.data.category_id);
      }
    } else return Toast({ type: "err", title: "دریافت اطلاعات سوابق" });
  }, [request]);

  useEffect(() => {
    getRecordByIdData();
  }, [getRecordByIdData]);

  return (
    <DrawerCM title="ویرایش سوابق">
      {isUploading ? (
        <ProgressBarCM value={uploadProgress} />
      ) : loading ? (
        <LoadingCM />
      ) : (
        <Grid container>
          <Grid item mt={3} xs={12}>
            <BackButton Route="/records" />
          </Grid>
          <StyledStack>
            <StyledTitle>ویرایش سوابق</StyledTitle>
            <Grid container>
              <Grid item md={4} xs={12} p={1.2}>
                <TextField
                  label="عنوان "
                  variant="outlined"
                  fullWidth
                  id="title"
                  {...register("title")}
                  error={Boolean(errors.title)}
                  helperText={errors.title?.message}
                />
              </Grid>

              <Grid item md={4} xs={12} p={1.2}>
                <ReactHookFormSelect
                  label="زیردسته"
                  control={control}
                  title="زیردسته"
                  id="subCategory"
                  {...register("subCategory")}
                  error={Boolean(errors.subCategory)}
                  helperText={errors.subCategory?.message}
                  onChange={(value) => setSelectedSubCategory(value)}
                >
                  {subCategoryList?.map((item, index) => (
                    <StyledMenuItem key={index} value={item.id}>
                      {item.title}
                    </StyledMenuItem>
                  ))}
                </ReactHookFormSelect>
              </Grid>
              <Grid item md={4} xs={12} p={1.2} sx={{ pt: 2.2 }}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleCheckChange}
                      />
                    }
                    label="نمایش در صفحه اصلی"
                  />
                </FormGroup>
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {recordsImage.url ? (
                  <Upload
                    isWeb={recordsImage.isWeb}
                    isLocal={!recordsImage.isWeb}
                    file={recordsImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeRecordsImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                ) : (
                  <Upload
                    isLocal={recordsImage.isChanged}
                    file={recordsImage.url}
                    refillable
                    type="add-image"
                    onChange={handleChangeRecordsImage}
                    width={"100%"}
                    height={"100%"}
                    objectFit="contain"
                  />
                )}
              </Grid>
              <Grid item md={6} xs={12} p={1.2}>
                {recordsVideo.url ? (
                  <UploadVideoCM
                    isWeb={recordsVideo.isWeb}
                    isLocal={!recordsVideo.isWeb}
                    file={recordsVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeRecordsVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                ) : (
                  <UploadVideoCM
                    isLocal={recordsVideo.isChanged}
                    file={recordsVideo.url}
                    refillable
                    type="add-video"
                    onChange={handleChangeRecordsVideo}
                    width={"100%"}
                    height={"100%"}
                  />
                )}
              </Grid>
              <Grid item xs={12} p={1.2}>
                {text && (
                  <Editor
                    enableToolbar={true}
                    onChange={handleChangeText}
                    defaultValue={text}
                    setDefaultStyle="font-family: IranSans; height: 40vh;"
                    setOptions={{
                      buttonList: buttonList.complex,
                      font: [
                        "IranSans",
                        "IranSansBold",
                        "Arial",
                        "Impact",
                        "Tahoma",
                      ],
                    }}
                  />
                )}
              </Grid>

              <ButtonContainer>
                <Button
                  startIcon={<MdDelete />}
                  variant="contained"
                  color="error"
                  sx={{ mt: 1, mr: 2 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handelShowModal();
                  }}
                >
                  حذف
                </Button>
                <Button
                  startIcon={<CheckIcon />}
                  variant="contained"
                  sx={{ mt: 1 }}
                  onClick={handleSubmit(onSubmit)}
                >
                  ذخیره
                </Button>
              </ButtonContainer>
            </Grid>
          </StyledStack>
          <DeleteModal
            show={showDeleteModal}
            onClose={handelShowModal}
            onDelete={handleDeleteItem.bind(null, records_id)}
          />
        </Grid>
      )}
    </DrawerCM>
  );
}
